import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  viewChild,
} from '@angular/core';
import { Browser } from '@core/services';

/**
 * ### **BottomSheet**
 *
 * Panel deslizante anclado al borde inferior de la pantalla. En escritorio se centra y se
 * comporta como un diálogo convencional. Se usa para el menú de navegación en móvil, el
 * acceso a la cuenta y los detalles rápidos del home.
 *
 * El contenido se proyecta con `<ng-content>`, de modo que quien lo abre decide qué mostrar.
 */
@Component({
  selector: 'app-bottom-sheet',
  imports: [],
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomSheet {
  private readonly browser = inject(Browser);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  private readonly dialog = viewChild<ElementRef<HTMLElement>>('dialog');

  /** Estado de apertura. Es `model` para permitir `[(open)]` desde el padre. */
  readonly open = model(false);

  /** Título del panel. */
  readonly title = input('');

  /** Texto de apoyo bajo el título. Si viene vacío no se renderiza. */
  readonly description = input('');

  constructor() {
    /**
     * Bloquea el scroll del documento mientras el panel está abierto: sin esto el fondo
     * se desplaza detrás del panel al hacer scroll táctil.
     */
    effect((onCleanup) => {
      if (!this.browser.isBrowser || !this.open()) return;

      const { body } = this.document;
      const previousOverflow = body.style.overflow;
      body.style.overflow = 'hidden';

      onCleanup(() => {
        body.style.overflow = previousOverflow;
      });
    });

    effect(() => {
      if (!this.browser.isBrowser || !this.open()) return;

      afterNextRender(() => this.dialog()?.nativeElement.focus(), {
        injector: this.injector,
      });
    });
  }

  /** Cierra el panel. */
  close(): void {
    this.open.set(false);
  }

  /** Cierra solo si el clic ocurrió sobre el fondo y no sobre el panel. */
  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }
}
